<template>
  <div class="post-composer-view" :style="[showSpinner ? { pointerEvents: 'none' } : {}]" v-if="showComposer">
    <div class="composer-view-close-overlay" @click.stop="emitClick"></div>
    <div class="post-composer-container-bg" style="background: white">
      <div class="post-composer-container">
        <div class="post-composer-top">
          <img class="composer-avatar" :src="user.picture" />
          <div class="composer-user-and-time">
            <div class="composer-user">
              {{ user.name }}
              <i class="post-composer-group-name" v-if="activityName">{{ activityName ? ` - ${activityName}` : '' }}</i>
            </div>
            <div class="composer-time">{{ formatComposerDate() }}</div>
          </div>
          <ion-icon style="cursor: pointer; margin: -10px -10px 0 auto; font-size: 46px; color: #aaaaaaaa" name="close-circle" @click.stop="emitClick"></ion-icon>
        </div>
        <div class="post-composer-middle">
          <div v-if="showSpinner" class="lds-spinner">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div contenteditable class="post-composer-textarea" ref="postTextArea" :placeholder="$t('postComposer.placeholder', { name: user.name })" @input="handleParsing" @paste.prevent="stripFormatting" v-focus></div>

          <div class="post-composer-media-container" v-if="selectedFiles.length > 0 || parsedImage || parsedVideo">
            <!-- VIDEO DISPLAY -->
            <iframe class="post-video" :src="parsedVideo" frameborder="0" allow="encrypted-media;" allowfullscreen v-if="parsedVideo"></iframe>

            <!-- MULTIPLE IMAGES DISPLAY -->
            <div class="multiple-images-container" v-if="selectedFiles.length > 0 && !parsedVideo">
              <div class="image-preview-item" v-for="(file, index) in selectedFiles" :key="index">
                <img class="post-composer-image" :src="file.preview" />
                <div class="image-remove-btn" @click="removeSelectedFile(index)">
                  <ion-icon name="close-circle"></ion-icon>
                </div>
              </div>
            </div>

            <!-- SINGLE IMAGE DISPLAY (for parsed links) -->
            <a :href="parsedLink" target="_blank" v-if="!parsedVideo && selectedFiles.length === 0 && parsedImage">
              <img class="post-composer-image" :src="parsedImage" />
              <div class="post-composer-parsed">
                <p class="parsed-site">{{ parsedSiteName }}</p>
                <p class="parsed-title">{{ parsedTitle }}</p>
              </div>
            </a>
          </div>
        </div>

        <div class="post-composer-bottom">
          <div class="post-bottom-attachments">
            <input multiple ref="fileInput" style="display: none" type="file" name="files[]" accept="image/*,video/*" @change="filesPickedHandler" />
            <div @click="$refs.fileInput.click()" style="cursor: pointer; display: flex; align-items: center" v-if="!parsedImage || enteredComment">
              <ion-icon class="post-actions-icon" name="image-outline"></ion-icon>
              <span style="margin-right: 10px">{{ selectedFiles.length > 0 ? $t('postComposer.addMoreImages') : $t('postComposer.insertImage') }}</span>
            </div>
            <div style="cursor: pointer; display: flex; align-items: center" v-if="parsedLink && parsedImage">
              <ion-icon class="post-actions-icon" name="link-outline"></ion-icon>
              <span @click="removeParsedLink">{{ $t('postComposer.removeLink') }}</span>
            </div>
            <div style="cursor: pointer; display: flex; align-items: center; margin-left: 15px" v-if="selectedFiles.length > 0">
              <ion-icon class="post-actions-icon" name="trash-outline"></ion-icon>
              <span @click="clearAllImages">{{ $t('postComposer.removeAllImages') }}</span>
            </div>
          </div>
          <div class="post-bottom-separator"></div>
          <div class="post-bottom-action" :class="{ disabled: debounced }" @click.prevent="sendPostDebounced">
            <div class="post-composer-post-btn">{{ $t('postComposer.post') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as dayjs from 'dayjs';
import api from '@/services/api';

export default {
  name: 'PostComposer',
  props: { showComposer: false, activityID: null, activityName: null, fatherId: null, commentRoomId: null, postId: null, post: null, enteredComment: null },
  directives: {
    focus: {
      inserted: function (el) {
        el.focus();
      },
    },
  },
  data() {
    return {
      textAreaValue: '',
      media: { images: [] },
      parsedImage: '',
      parsedTitle: '',
      parsedLink: '',
      parsedSiteName: '',
      parsedVideo: '',
      selectedFiles: [],
      showSpinner: false,
      debounced: false,
    };
  },
  methods: {
    formatComposerDate() {
      const day = dayjs(Date.now()).format('DD');
      const month = dayjs(Date.now()).format('MMMM');
      const time = dayjs(Date.now()).format('HH:mm');
      return this.$t('postComposer.dateFormat', { day, month, time });
    },
    handleParsing(e) {
      if (!this.parsedImage) {
        let pattern1 = /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi;
        let pattern2 = /(http(s)?:\\)?([\w-]+\.)?[\w-]+\.+[.com|.co|.in|.org|.edu|.gov|.dev|.io]+(\[\?%&=]*)?/;

        let regex = new RegExp(pattern1);
        this.textAreaValue = e.target.innerText;
        let wordsArray = this.textAreaValue.trim().split(/\s+/);

        let link = wordsArray.slice(-1)[0];
        if (link.match(regex)) {
          if (link.charAt(0) == 'h') {
            this.parsedLink = link;
            this.parseOpenGraph(link);
          } else {
            this.parsedLink = `https://${link}`;
            this.parseOpenGraph(`https://${link}`);
          }
        }
      } else {
        this.textAreaValue = e.target.innerText;
      }
    },
    parseOpenGraph(link) {
      let headers = new Headers();
      headers.append('Content-Type', 'application/json');
      headers.append('Accept', 'application/json');

      let raw = JSON.stringify({
        url: link,
      });

      let requestOptions = {
        headers: headers,
        method: 'POST',
        mode: 'cors',
        body: raw,
        redirect: 'follow',
      };
      fetch('https://parse.blitzar.com.br/ogScrap', requestOptions)
        .then((response) => response.text())
        .catch((error) => {
          console.error('fetch response error', error);
        })
        .then((result) => {
          let parsedResult;
          try {
            parsedResult = JSON.parse(result);
          } catch (error) {
            parsedResult = result;
            console.error('parse response error', error);
          }
          this.$nextTick((_) => {
            this.parsedTitle = parsedResult.data ? parsedResult.data.ogTitle : '';
            this.parsedLink = parsedResult.data.ogUrl ? parsedResult.data.ogUrl : this.parsedLink;
            this.parsedSiteName = parsedResult.data.ogSiteName ? parsedResult.data.ogSiteName : '';
            this.parsedImage = parsedResult.data.ogImage ? parsedResult.data.ogImage[0] : '';

            this.media = {};
            this.media.parsedTitle = this.parsedTitle;
            this.media.parsedLink = this.parsedLink;
            this.media.parsedImage = this.parsedImage;
            this.media.parsedSiteName = this.parsedSiteName;

            const youtubeId = this.getYoutubeId(this.parsedLink) ? this.getYoutubeId(this.parsedLink) : false;
            if (youtubeId) {
              this.media.video = `https://www.youtube.com/embed/${youtubeId}?playsinline=1`;
              this.media.parsedLink = '';
              this.media.parsedTitle = '';
              this.media.parsedImage = '';
              this.media.parsedSiteName = '';
            }
          });
        })
        .catch((error) => {
          console.error('fetch result error', error);
        });
    },
    getYoutubeId(url) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return match && match[2].length === 11 ? match[2] : null;
    },
    removeParsedLink() {
      this.parsedLink = '';
      this.parsedImage = '';
      this.parsedTitle = '';
      this.parsedSiteName = '';
      this.media.parsedLink = '';
      this.media.parsedTitle = '';
      this.media.parsedImage = '';
      this.media.parsedSiteName = '';
    },
    sendPostDebounced() {
      if (this.debounced) return;
      this.debounced = true;
      this.sendPost();
      setTimeout(() => {
        this.debounced = false;
      }, 5000);
    },
    sendPost() {
      if (this.selectedFiles.length > 0) {
        this.uploadAllImages().then(() => {
          if (this.$store.state.user.id) {
            if (this.postId) {
              api
                .put(`event/${this.$store.state.eventId}/comment/${this.postId}`, {
                  message: this.textAreaValue,
                  media: JSON.stringify(this.media),
                })
                .then((response) => {
                  this.media = { images: [] };
                  api.post(`sessionAction/`, {
                    action: 'Edit Post',
                    properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, post: this.postId }),
                    userid: this.$store.state.user.id,
                    sessionid: this.$store.state.user.token,
                    event_id: this.$store.state.eventId,
                  });
                  this.emitClick();
                })
                .catch((err, data) => {
                  console.error('error', err.response, data);
                  alert(this.$t('postComposer.errors.editPostFailed'));
                });
            } else {
              api
                .post(`event/${this.$store.state.eventId}/comment`, {
                  activity_id: this.activityID,
                  comment_room_id: this.commentRoomId,
                  user_id: this.$store.state.user.id,
                  father_id: this.fatherId ? this.fatherId : undefined,
                  message: this.textAreaValue ? this.textAreaValue : ' ',
                  tags: JSON.stringify({ like: 0 }),
                  highlight: false,
                  media: JSON.stringify(this.media),
                })
                .then((response) => {
                  if (this.fatherId) {
                    api.post(`sessionAction/`, {
                      action: 'Comment New Comentary',
                      properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, father: this.fatherId, message: this.textAreaValue }),
                      userid: this.$store.state.user.id,
                      sessionid: this.$store.state.user.token,
                      event_id: this.$store.state.eventId,
                    });
                  } else {
                    api.post(`sessionAction/`, {
                      action: 'Comment New Post',
                      properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, message: this.textAreaValue }),
                      userid: this.$store.state.user.id,
                      sessionid: this.$store.state.user.token,
                      event_id: this.$store.state.eventId,
                    });
                  }

                  this.emitClick();
                })
                .catch((err, data) => {
                  console.error('Catch senPost', data, err);
                  alert(this.$t('postComposer.errors.postFailed'));
                });
            }
          }
        });
      } else {
        if (this.$store.state.user.id) {
          if (this.postId) {
            api
              .put(`event/${this.$store.state.eventId}/comment/${this.postId}`, {
                message: this.textAreaValue,
                media: JSON.stringify(this.media),
              })
              .then((response) => {
                this.media = { images: [] };
                api.post(`sessionAction/`, {
                  action: 'Edit Post',
                  properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, post: this.postId, message: this.textAreaValue }),
                  userid: this.$store.state.user.id,
                  sessionid: this.$store.state.user.token,
                  event_id: this.$store.state.eventId,
                });
                this.emitClick();
              })
              .catch((err, data) => {
                console.error('error', err.response, data);
                alert(this.$t('postComposer.errors.editPostFailed'));
              });
          } else {
            api
              .post(`event/${this.$store.state.eventId}/comment`, {
                activity_id: this.activityID,
                comment_room_id: this.commentRoomId,
                user_id: this.$store.state.user.id,
                father_id: this.fatherId ? this.fatherId : undefined,
                message: this.textAreaValue,
                tags: JSON.stringify({ like: 0 }),
                highlight: false,
                media: JSON.stringify(this.media),
              })
              .then((response) => {
                if (this.fatherId) {
                  api.post(`sessionAction/`, {
                    action: 'Comment New Comentary',
                    properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, father: this.fatherId, message: this.textAreaValue }),
                    userid: this.$store.state.user.id,
                    sessionid: this.$store.state.user.token,
                    event_id: this.$store.state.eventId,
                  });
                } else {
                  api.post(`sessionAction/`, {
                    action: 'Comment New Post',
                    properties: JSON.stringify({ route: this.$route.name, params: this.$route.params, message: this.textAreaValue }),
                    userid: this.$store.state.user.id,
                    sessionid: this.$store.state.user.token,
                    event_id: this.$store.state.eventId,
                  });
                }
                this.emitClick();
              })
              .catch((err) => {
                if (this.textAreaValue.length > 0 && err.response?.data?.comment_spam === true) {
                  this.$notify.warning({
                    title: this.$t('postComposer.notifications.repeatedMessage'),
                    message: this.$t('postComposer.notifications.tryAgainLater'),
                    position: 'top-right',
                    offset: 120,
                  });
                } else {
                  this.$notify.warning({
                    title: this.$t('postComposer.notifications.emptyMessage'),
                    message: this.$t('postComposer.notifications.writeBeforePosting'),
                    position: 'top-right',
                    offset: 120,
                  });
                }
              });
          }
        }
      }
    },
    getPost(postId) {
      if (postId) {
        api
          .get(`event/${this.$store.state.eventId}/comment/${postId}`)
          .then((response) => {
            const posts = response.data;
            if (posts) {
              this.textAreaValue = posts.message;
              try {
                media = JSON.parse(posts.media);
              } catch (error) {
                media = [];
              }
            }
          })
          .catch((err, data) => {
            console.error('error', err.response, data);
            alert(this.$t('postComposer.errors.editFailed'));
          });
      }
    },
    emitClick() {
      // Clean up object URLs to prevent memory leaks (only for new files)
      this.selectedFiles.forEach((file) => {
        if (!file.isExisting && file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });

      this.selectedFiles = [];
      this.textAreaValue = '';
      this.media = {};
      this.parsedImage = '';
      this.parsedTitle = '';
      this.parsedLink = '';
      this.parsedSiteName = '';
      this.parsedVideo = '';
      this.$emit('closeClick');
    },
    stripFormatting(e) {
      let text = e.clipboardData.getData('text/plain');

      let parser = new DOMParser();
      let parsedHTML = parser.parseFromString(text, 'text/html');
      e.target.textContent += text;
      this.handleParsing(e);
    },
    filesPickedHandler(event) {
      this.parsedVideo = '';

      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];

        // Check if it's a video file
        if (file.name.endsWith('mp4') || file.name.endsWith('mov') || file.name.endsWith('m4v')) {
          // For videos, only allow one and clear images
          this.selectedFiles = [];
          this.parsedImage = 'video';
          this.parsedVideo = URL.createObjectURL(file);
          this.parsedTitle = file.name;
          this.selectedFiles.push({
            file: file,
            preview: '',
            isVideo: true,
          });
          break; // Only allow one video
        } else {
          // For images, add to the array
          const fileObj = {
            file: file,
            preview: URL.createObjectURL(file),
            isVideo: false,
          };
          this.selectedFiles.push(fileObj);
        }
      }

      // Clear the file input so the same files can be selected again if needed
      event.target.value = '';
    },
    removeSelectedFile(index) {
      const fileToRemove = this.selectedFiles[index];

      // Only revoke object URL if it's a new file (not existing)
      if (!fileToRemove.isExisting && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      this.selectedFiles.splice(index, 1);

      // If it was a video, clear video-related data
      if (this.selectedFiles.length === 0) {
        this.parsedVideo = '';
        this.parsedImage = '';
      }
    },
    clearAllImages() {
      // Revoke object URLs only for new files (not existing ones)
      this.selectedFiles.forEach((file) => {
        if (!file.isExisting && file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      this.selectedFiles = [];
      this.parsedVideo = '';
      this.parsedImage = '';
      this.parsedTitle = '';
    },
    uploadImage(file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tag', 'Feed');

      return api
        .post(`activity/${this.$route.params.stream}/materialUpload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            this.showSpinner = true;
            if (progressEvent.loaded === progressEvent.total) {
              this.showSpinner = false;
            }
          },
        })
        .then((response) => {
          return response.data ? response.data.url : null;
        })
        .catch((err) => {
          console.error(err);
          throw err;
        });
    },
    async uploadAllImages() {
      if (this.selectedFiles.length === 0) {
        return;
      }

      this.showSpinner = true;

      // Separate existing images from new files to upload
      const existingImages = this.selectedFiles.filter((fileObj) => fileObj.isExisting).map((fileObj) => fileObj.preview);

      const newFilesToUpload = this.selectedFiles.filter((fileObj) => !fileObj.isExisting && fileObj.file);

      try {
        let uploadedUrls = [];

        // Upload new files
        if (newFilesToUpload.length > 0) {
          const uploadPromises = newFilesToUpload.map((fileObj) => {
            return this.uploadImage(fileObj.file);
          });
          uploadedUrls = await Promise.all(uploadPromises);
        }

        // Combine existing images with newly uploaded ones
        const allImageUrls = [...existingImages, ...uploadedUrls.filter((url) => url)];

        // Clear any existing images in media
        this.media.images = [];
        this.media.video = '';

        // Separate videos from images
        allImageUrls.forEach((url) => {
          if (url.endsWith('.mp4')) {
            this.media.video = url;
          } else {
            this.media.images.push(url);
          }
        });

        this.showSpinner = false;
      } catch (error) {
        this.showSpinner = false;
        throw error;
      }
    },
  },
  beforeMount() {
    this.dayjs = dayjs;
    this.user = this.$store.state.user;

    if (this.postId) {
      this.$nextTick(() => {
        this.$refs.postTextArea.innerHTML = this.post.message;
        this.textAreaValue = this.post.message;
        this.media = this.post.media;
        this.parsedVideo = this.post.media.video ? this.post.media.video : '';

        // Handle existing images for editing
        if (this.post.media.images && this.post.media.images.length > 0) {
          this.selectedFiles = this.post.media.images.map((imageUrl, index) => ({
            file: null, // No file object for existing images
            preview: imageUrl,
            isVideo: false,
            isExisting: true, // Flag to identify existing images
          }));
        }

        if (this.post.media.parsedImage) {
          this.parsedImage = this.post.media.parsedImage;
          this.parsedTitle = this.post.media.parsedTitle;
          this.parsedSiteName = this.post.media.parsedSiteName;
          this.parsedLink = this.post.media.parsedLink;
        } else if (this.post.media.images && this.post.media.images.length > 0) {
          this.parsedImage = this.post.media.images[0];
        }
      });
    } else if (this.enteredComment) {
      this.$nextTick(() => {
        this.$refs.postTextArea.innerText = this.enteredComment;
        this.textAreaValue = this.enteredComment;
      });
    } else {
      this.media = this.post?.media || { images: [] };
      this.parsedImage = this.post?.media?.parsedImage;
    }
  },
};
</script>

<style lang="scss">
[contenteditable][placeholder]:empty:before {
  content: attr(placeholder);
  position: absolute;
  color: var(--feedSocial-subColor);
  background-color: transparent;
}
.post-composer-view {
  position: fixed;
  inset: 0;
  width: 100vw;
  background-color: #000000cc;
  z-index: 100;

  @include media('<=phone-plus') {
    z-index: 10000;
  }

  display: flex;
  justify-content: center;
  align-items: center;
}
.composer-view-close-overlay {
  position: absolute;
  width: 100vw;
  height: 100dvh;
}
.post-composer-container-bg {
  position: relative;
  background-color: var(--main-color);
  width: 600px;
  height: 78vh;
  margin-top: 68px;
  border-radius: var(--feedSocial-borderRadius);

  @include media('<=phone-plus') {
    width: 100%;
    height: 100%;
    margin: 0;
  }
  @media (max-height: 460px) {
    margin-top: 10px;
    overflow-y: auto;
  }
}
.post-composer-container {
  position: relative;
  background-color: var(--feedSocial-bgColor);
  width: 600px;
  height: 100%;
  padding-bottom: 14px;
  border-radius: var(--feedSocial-borderRadius);

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 101;
  @include media('<=phone-plus') {
    width: 100%;
    height: 100%;
    padding: 10px 0 0 0;
  }

  @media (max-height: 460px) {
    height: 100%;
    padding: 10px 0 0px 0;
    overflow-y: auto;
  }
}
.post-composer-top {
  position: relative;
  width: 100%;
  height: 12%;
  // background-color: #303030;
  display: flex;
  align-items: center;
  padding: 3%;
  @media (max-height: 460px) {
    margin-top: 10px;
  }
  .composer-avatar {
    position: relative;
    object-fit: cover;
    object-position: center;
    background-color: transparent;
    width: 50px;
    height: 50px;
    // border: 4px solid white;
    border-radius: 50%;
    margin-right: 2%;
  }
  .composer-user {
    color: var(--feedSocial-textColor);
    font-size: 1.2rem;
  }
  .composer-time {
    color: var(--feedSocial-subColor);
    font-size: 0.8rem;
  }
}
.post-composer-middle {
  position: relative;
  width: 100%;
  height: 80%;
  // background-color: #303030;
  display: flex;
  flex-direction: column;
  padding: 0;
}
.post-composer-group-name {
  font-size: 1.2rem;
  color: var(--feedSocial-subColor);
}
.post-composer-textarea {
  position: relative;
  height: 100%;
  width: 100%;
  resize: none;
  border: none;
  outline: none;
  padding: 1% 4% 4%;
  color: var(--feedSocial-textColor);
  font-size: 1.2rem;
  text-overflow: clip;
  overflow: auto;
}
.post-composer-media-container {
  position: relative;
  // height: 40%;
  // background-color: red;
  .post-composer-image {
    position: relative;
    object-fit: cover;
    width: 100%;
    max-height: 400px;
  }
  .post-composer-parsed {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0 2% 1%;

    .parsed-title {
      color: var(--feedSocial-textColor);
      font-size: 1.1rem;
      opacity: 0.7;
    }
    .parsed-site {
      color: var(--feedSocial-textColor);
      font-size: 0.8rem;
      opacity: 0.7;
      margin: 1% 0;
    }
  }
}

.multiple-images-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  padding: 10px;
  max-height: 400px;
  overflow-y: auto;
}

.image-preview-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;

  .post-composer-image {
    width: 100%;
    height: 150px;
    object-fit: cover;
    display: block;
  }

  .image-remove-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.9);
    }

    ion-icon {
      color: white;
      font-size: 20px;
    }
  }
}

.post-composer-bottom {
  position: relative;
  width: 100%;
  height: 20%;
  // background-color: #303030;
  color: var(--feedSocial-subColor);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 1.5% 3%;
  .post-bottom-attachments {
    width: 100%;
    display: flex;
    // justify-content: space-evenly;
    // margin-bottom: 2%;

    span {
      font-size: 0.7rem;
    }
  }
  .post-bottom-action {
    cursor: pointer;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    width: 100%;
    padding: 2% 0;
    margin: 2% 0;
    border-radius: var(--feedSocial-borderRadius);
    background-color: var(--feedSocial-postButtonBgColor);

    .post-composer-post-btn {
      position: relative;
      color: var(--feedSocial-postButtonTextColor);

      display: flex;
      align-items: center;
      user-select: none;
    }

    &.disabled {
      opacity: 0.1;
      cursor: not-allowed;
    }
  }
}
</style>
